import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Globe, HeadphonesIcon, Clock, Check } from 'lucide-react';
import WebsiteNav from './WebsiteNav';
import WebsiteFooter from './WebsiteFooter';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function AboutPage() {
  return (
    <div className="bg-[#0B0F19] min-h-screen text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <WebsiteNav />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.25em] uppercase text-blue-400 mb-4">About Us</motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Built by People Who Understand Construction
            </motion.h1>
            <motion.p variants={fadeUp} className="text-slate-400 mt-6 text-lg leading-relaxed">
              ProjectBook Pro&trade; is developed by <a href="https://www.sgkapps.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors">SGK Apps</a>, a product of <strong className="text-slate-300">VRV Technical & Technology Solutions FZ LLC</strong>. We combine deep construction industry expertise with modern software engineering to deliver tools that actually work on site.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Company Section */}
      <section className="py-20 bg-[#0E1220]" data-testid="company-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/392031/pexels-photo-392031.jpeg?auto=compress&w=800"
                alt="Construction"
                className="rounded-xl border border-white/10 shadow-2xl"
              />
            </div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.h2 variants={fadeUp} className="text-3xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Why ProjectBook Pro?
              </motion.h2>
              <div className="space-y-4">
                {[
                  'Designed specifically for the construction industry — not adapted from generic tools',
                  'UAE tax and labor law compliant out of the box',
                  'Multi-tenant architecture for complete data isolation',
                  '11 role-based access levels tailored to construction workflows',
                  'Double-entry accounting with auto journal entries',
                  'From project planning to financial close-out in one system',
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeUp} className="flex items-start gap-3">
                    <Check size={16} className="text-blue-400 mt-1 shrink-0" />
                    <p className="text-sm text-slate-300 leading-relaxed">{item}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.2em] uppercase text-yellow-500 mb-3">Our Promise</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What Sets Us Apart
            </motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Shield, color: '#3B82F6', title: 'Enterprise Security', desc: 'Multi-tenant data isolation, encrypted connections, and complete audit trails.' },
              { icon: Globe, color: '#10B981', title: 'UAE Compliant', desc: 'Built-in VAT/GST support, labor law compliance, and regional tax reporting.' },
              { icon: HeadphonesIcon, color: '#F59E0B', title: 'Dedicated Support', desc: 'Direct access to our team for training, onboarding, and technical assistance.' },
              { icon: Clock, color: '#8B5CF6', title: 'Annual Licensing', desc: 'Simple annual subscription with no hidden fees. All modules included.' },
            ].map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="bg-[#1A1F36] border border-white/8 rounded-xl p-6 h-full transition-all duration-300 hover:-translate-y-1 hover:border-white/20" data-testid={`value-card-${i}`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: v.color + '18' }}>
                    <v.icon size={20} style={{ color: v.color }} />
                  </div>
                  <h3 className="font-semibold text-sm text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{v.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{v.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-24 bg-[#0E1220] relative overflow-hidden" data-testid="contact-section">
        <div className="absolute top-10 right-1/4 w-72 h-72 bg-blue-500/15 rounded-full blur-[100px] pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-3">Get In Touch</p>
              <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Ready to Get Started?
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Reach out for a demo, pricing information, or to discuss how ProjectBook Pro&trade; can transform your project management.
              </p>
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Email</p>
                  <a href="mailto:info@sgkapps.com" className="text-white font-medium hover:text-blue-400 transition-colors">info@sgkapps.com</a>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Website</p>
                  <a href="/website" className="text-white font-medium hover:text-blue-400 transition-colors">www.projectbookpro.com</a>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Company</p>
                  <p className="text-slate-300 text-sm"><a href="https://www.sgkapps.com" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">SGK Apps</a></p>
                  <p className="text-slate-500 text-xs">A product of VRV Technical & Technology Solutions FZ LLC</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="bg-[#1A1F36] border border-white/10 rounded-xl p-8 text-center w-full">
                <img src="/sidebar-icon.png" alt="" className="h-16 w-16 mx-auto mb-4 rounded-xl" />
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ProjectBook Pro<sup className="text-[8px]">TM</sup>
                </h3>
                <p className="text-xs text-slate-500 mb-6">PLAN &middot; BUILD &middot; DELIVER</p>
                <Link
                  to="/demo"
                  className="inline-block w-full px-6 py-3 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-lg transition-all text-sm shadow-lg shadow-blue-500/25"
                  data-testid="about-demo-btn"
                >
                  Try Live Demo
                </Link>
                <a
                  href="mailto:info@sgkapps.com"
                  className="inline-block w-full mt-3 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold rounded-lg transition-all text-sm"
                >
                  Request Pricing
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
